import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, Toolbar, useTheme, useMediaQuery } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import MainAppBar from './components/layout/AppBar';
import SideDrawer from './components/layout/Drawer';
import Dashboard from './pages/Dashboard';
import Cards from './pages/Cards';
import Performance from './pages/Performance';
import Financeiro from './pages/Financeiro';
import CacheDebug from './pages/CacheDebug';
import InspecoesSeguranca from './pages/InspecoesSeguranca';
import EquipesAlertas from './pages/EquipesAlertas';
import Login from './pages/Login';
import TrocarSenha from './pages/TrocarSenha';
import ProtectedRoute from './components/ProtectedRoute';
import AdminDashboard from './pages/admin/AdminDashboard';
import HealthPage from './pages/configuracoes/Health';
import UsuariosList from './pages/admin/UsuariosList';
import UsuarioForm from './pages/admin/UsuarioForm';
import UsuarioRoles from './pages/admin/UsuarioRoles';
import RolesList from './pages/admin/RolesList';
import RoleForm from './pages/admin/RoleForm';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { queryClient } from './config/queryClient';

const drawerWidth = 240;

function App() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Box
            sx={{
              '& svg circle[fill*="url(#a-cl-1)"]': {
                display: 'none !important',
              },
              '& circle[cx="316.5"][cy="316.5"][r="316.5"]': {
                display: 'none !important',
              },
            }}
          >
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/trocar-senha" element={<TrocarSenha />} />
              
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Box sx={{ display: 'flex', height: '100vh' }}>
                      <CssBaseline />
                      <MainAppBar open={open} handleDrawerOpen={handleDrawerOpen} drawerWidth={drawerWidth} />
                      <SideDrawer open={open} handleDrawerClose={handleDrawerClose} drawerWidth={drawerWidth} />
                      <Box
                        component="main"
                        sx={{
                          flexGrow: 1,
                          p: { xs: 2, sm: 3 },
                          width: { 
                            xs: '100%',
                            sm: open ? `calc(100% - ${drawerWidth}px)` : '100%'
                          },
                          backgroundColor: (theme) => theme.palette.background.default,
                          overflow: 'auto',
                          transition: muiTheme.transitions.create(['width', 'margin'], {
                            easing: muiTheme.transitions.easing.sharp,
                            duration: muiTheme.transitions.duration.enteringScreen,
                          }),
                        }}
                      >
                        <Toolbar />
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/cards" element={<Cards />} />
                          <Route path="/equipes/performance" element={<Performance />} />
                          <Route path="/equipes/inspecoes-seguranca" element={<InspecoesSeguranca />} />
                          <Route path="/equipes/alertas" element={<EquipesAlertas />} />
                          <Route path="/equipes/turnos" element={<Navigate to="/equipes/performance" replace />} />
                          <Route path="/equipes/ranking" element={<Navigate to="/equipes/performance" replace />} />
                          <Route path="/equipes" element={<Navigate to="/equipes/performance" replace />} />
                          <Route path="/financeiro" element={<Financeiro />} />
                          <Route path="/cache-debug" element={<CacheDebug />} />
                          <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
                          <Route path="/admin/usuarios" element={<ProtectedRoute requiredRole="admin"><UsuariosList /></ProtectedRoute>} />
                          <Route path="/admin/usuarios/criar" element={<ProtectedRoute requiredRole="admin"><UsuarioForm /></ProtectedRoute>} />
                          <Route path="/admin/usuarios/editar/:id" element={<ProtectedRoute requiredRole="admin"><UsuarioForm /></ProtectedRoute>} />
                          <Route path="/admin/usuarios/roles/:usuarioId" element={<ProtectedRoute requiredRole="admin"><UsuarioRoles /></ProtectedRoute>} />
                          <Route path="/admin/roles" element={<ProtectedRoute requiredRole="admin"><RolesList /></ProtectedRoute>} />
                          <Route path="/admin/roles/criar" element={<ProtectedRoute requiredRole="admin"><RoleForm /></ProtectedRoute>} />
                          <Route path="/admin/roles/editar/:id" element={<ProtectedRoute requiredRole="admin"><RoleForm /></ProtectedRoute>} />
                          <Route path="/configuracoes/health" element={<HealthPage />} />
                        </Routes>
                      </Box>
                    </Box>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
          </Box>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
